import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Sensor } from '../../shared/models';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { MapDetails } from '@models/MapFavorite/mapDetails.model';
import { MapFavoriteInfo } from '@models/MapFavorite/mapFavoriteInfo.model';
import { SensorReadingSelection } from '@models/Sensor/sensorReadingSelection.model';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import { AppComponent } from 'app/app.component';
import { CommonShared } from 'app/shared/classes/common';
import { encodeDateRange } from 'app/shared/classes/dateRangeCodec';
import { SelectionReadingsLoader } from 'app/shared/classes/selectionReadingsLoader';
import { MapFavoriteSnapshot } from 'app/shared/components/map-favorite-bar/map-favorite-bar.component';
import { MapFavoriteComponent } from 'app/shared/components/map-favorite/map-favorite.component';
import { SensorReadingSelectorComponent } from 'app/shared/components/sensor-reading-selector/sensor-reading-selector.component';
import { ToastrService } from 'ngx-toastr';


// Persisted "last used" state so the page comes back the way the user left it, open
// popups included. version 2 replaced the old single-group shape, which is ignored.
interface LastUsedMapSettings {
  version: number;
  sensorIds: number[];
  sensorGroupIds: number[];
  readingTypeIds: number[];
  dateRange: string;            // preset label or "start,end"
  timeZone: string;
  favoriteId?: number;
  mapDetails?: MapDetails;
}


// GeoMap Readings: pick sensors / groups, reading types and a date range in the shared
// left panel, see the readings as pins on the shared map (kig-map-favorite), and save
// the whole thing (selection, map view, open pin popups, labels) as a Map Favorite.
@Component({
  standalone: false,
  selector: 'app-map-readings',
  templateUrl: './map-readings.component.html',
  styleUrls: [ './map-readings.component.scss' ]
})
export class MapReadingsComponent implements OnInit, AfterViewInit {

  @ViewChild(SensorReadingSelectorComponent) selector: SensorReadingSelectorComponent;
  @ViewChild(MapFavoriteComponent) mapComponent: MapFavoriteComponent;

  private static readonly LAST_USED_SETTINGS_KEY = 'MapReadingsLastUsed';
  private static readonly LAST_USED_VERSION = 2;

  common = new CommonShared();
  username = 'unknown';

  // What the shared map shows.
  mapSensors: Sensor[] = [];
  mapReadings: SensorReadingInfo[] = [];
  mapSavedDetails: MapDetails | null = null;
  private pendingMapDetails: MapDetails | null = null;   // handed to the map with the next readings
  private latestMapDetails: MapDetails | null = null;

  // A favorite / last used view that has been chosen but that the map has not reported
  // back yet. The last used cache stores it instead of the map's (still old) view, so
  // leaving the page right after picking a favorite still remembers that favorite.
  private unconfirmedMapDetails: MapDetails | null = null;
  private detailsHandedToMap = false;
  // True from picking a favorite until the left panel has restored its selection; the
  // live selection is the previous one until then, so nothing is cached in between.
  private applyingFavorite = false;

  // Current selection (from the shared left panel) and the readings loaded for it.
  private selection: SensorReadingSelection | null = null;
  // Fetches the readings per reading type and caches them, so re-checking a type costs
  // no call. Cleared whenever the sensors / date range / time zone change.
  private readingsLoader: SelectionReadingsLoader;
  private lastUsedRestoreDone = false;

  // ---- Map favorites (the controls live in kig-map-favorite-bar) ----
  public globalSwitch: boolean;
  // The selected favorite, with its permissions.
  public selectedMapFavorite: MapFavoriteInfo | undefined;
  // The favorite remembered from the last visit, shown in the bar once its list has loaded.
  public favoriteIdToSelectAfterLoad: number | undefined;

  // The map favorite a link asked to open (?mapFavoriteId=, e.g. from the home page search)
  public linkedMapFavoriteId: number | undefined;

  constructor(public toastr: ToastrService, public global: AppComponent, public _sensorService: SensorService,
              public _userService: UserService, route: ActivatedRoute) {
    this.globalSwitch = global.globalSwitch;
    this.linkedMapFavoriteId = Number(route.snapshot.queryParamMap.get('mapFavoriteId')) || undefined;
    this.readingsLoader = new SelectionReadingsLoader(_sensorService);
  }


  ngOnInit(): void {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
      },
      () => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  ngAfterViewInit(): void {
    // After this change-detection pass, so a restore that completes immediately cannot
    // change bound values mid-check.
    setTimeout(() => this.restoreLastUsed());
  }


  // ------------------------------------------------------------ selection / readings

  onSelectionChanged(selection: SensorReadingSelection): void {
    this.selection = selection;

    if (selection.reason === 'restore') {
      this.applyingFavorite = false;   // the panel now holds the favorite's selection
    }

    // A hand edit means the selection no longer matches the loaded favorite.
    if (selection.byUser) {
      this.selectedMapFavorite = undefined;
      this.favoriteIdToSelectAfterLoad = undefined;
    }

    switch (selection.reason) {
      case 'source':
        this.saveLastUsed();
        return;

      case 'readingTypes':
        break;   // only the not-yet-loaded types need fetching

      default:
        // The cached readings belong to the previous sensors / dates / time zone.
        this.readingsLoader.clear();
        break;
    }

    this.ensureLoaded();
  }


  refreshReadings(): void {
    this.readingsLoader.clear();
    this.ensureLoaded();
  }


  private ensureLoaded(): void {
    const selection = this.selection;
    if (selection == undefined) {
      this.publish([]);
      return;
    }

    this.readingsLoader.load({
      sensors: selection.sensors,
      readingTypes: selection.readingTypes,
      availableReadingTypes: selection.availableReadingTypes,
      dateRange: selection.dateRange,
      timeZone: selection.timeZone,
      timeZoneOffset: selection.timeZoneOffset
    }).subscribe(
      result => {
        if (result == null) {
          return;   // a newer selection superseded this request
        }

        this.publish(result.readings);

        if (result.fetched) {
          if (result.fetchedCount > 0) {
            this.toastr.success(result.fetchedCount + ' sensor readings loaded successfully');
          } else {
            this.toastr.warning('No sensor readings found for the selection.');
          }
        }
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor readings. Error: ' + err.message);
      });
  }


  // Hand the map the readings of the checked types for the selected sensors, with GPS
  // resolved. Saved view / popups (favorite or last used) go along with the readings
  // they belong to, so the map can find its pins.
  private publish(readings: SensorReadingInfo[]): void {
    this.mapReadings = readings;
    this.mapSensors = this.selection?.sensors ?? [];

    if (this.pendingMapDetails != null) {
      // Saved view / popups are on their way to the map. Do not save the last used
      // state yet: the map still shows its defaults and would overwrite them.
      this.mapSavedDetails = this.pendingMapDetails;
      this.pendingMapDetails = null;
      this.detailsHandedToMap = true;
      this.saveLastUsed();   // stores the not-yet-confirmed view, never the map's old one
      return;
    }

    this.saveLastUsed();
  }


  // The map reports every view / popup change; keep the latest for saving.
  onMapDetailsChanged(details: MapDetails): void {
    this.latestMapDetails = details;

    // Once the map has been given a chosen view, its next report is that view (or the
    // user's change to it), so the live state can be cached again.
    if (this.detailsHandedToMap) {
      this.unconfirmedMapDetails = null;
      this.detailsHandedToMap = false;
    }
    this.saveLastUsed();
  }


  private currentMapDetails(): MapDetails | undefined {
    const details = this.unconfirmedMapDetails ?? this.mapComponent?.getMapDetails() ?? this.latestMapDetails ?? undefined;
    if (details == undefined) {
      return undefined;
    }
    return { ...details, timeZone: this.selection?.timeZone };
  }


  // ------------------------------------------------------------ last used

  private saveLastUsed(): void {
    const selection = this.selection;
    if (!this.lastUsedRestoreDone || this.applyingFavorite || selection == undefined || selection.sensors.length === 0) {
      return;
    }

    const settings: LastUsedMapSettings = {
      version: MapReadingsComponent.LAST_USED_VERSION,
      sensorIds: selection.sensors.map(s => s.id),
      sensorGroupIds: selection.sensorGroups.map(g => g.id),
      readingTypeIds: selection.readingTypes.map(t => t.id),
      dateRange: encodeDateRange(selection.dateRangeName, selection.dateRange),
      timeZone: selection.timeZone,
      // Until the favorites list has loaded (right after a reload) the remembered
      // favorite is still waiting to be selected; keep it rather than dropping it.
      favoriteId: this.selectedMapFavorite?.id ?? this.favoriteIdToSelectAfterLoad,
      mapDetails: this.currentMapDetails()
    };

    try {
      localStorage.setItem(MapReadingsComponent.LAST_USED_SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // storage full / blocked: the page just does not remember
    }
  }


  private restoreLastUsed(): void {
    const saved = localStorage.getItem(MapReadingsComponent.LAST_USED_SETTINGS_KEY);
    let settings: LastUsedMapSettings | undefined;

    try {
      settings = (saved != undefined && saved !== '') ? JSON.parse(saved) : undefined;
    } catch {
      settings = undefined;   // malformed
    }

    if (settings == undefined || settings.version !== MapReadingsComponent.LAST_USED_VERSION || !(settings.sensorIds?.length > 0)) {
      this.lastUsedRestoreDone = true;   // nothing (usable) saved, e.g. the old single-group shape
      return;
    }

    this.favoriteIdToSelectAfterLoad = settings.favoriteId;
    this.pendingMapDetails = settings.mapDetails ?? null;
    this.unconfirmedMapDetails = settings.mapDetails ?? null;
    this.detailsHandedToMap = false;
    this.lastUsedRestoreDone = true;

    this.selector.applySelection({
      sensorIds: settings.sensorIds,
      sensorGroupIds: settings.sensorGroupIds ?? [],
      readingTypeIds: settings.readingTypeIds ?? [],
      dateRange: settings.dateRange,
      timeZone: settings.timeZone
    });
    // The bar shows the remembered favorite (favoriteIdToSelectAfterLoad) once its list loads.
  }


  // ------------------------------------------------------------ map favorites
  //
  // The dropdown, Save / Delete and the save dialog live in kig-map-favorite-bar. The page
  // supplies what to save (snapshot) and applies a picked favorite (applyFavorite).

  isMapFavoriteReadyToSave(): boolean {
    return this.selection != undefined && this.selection.sensors.length > 0 && this.selection.readingTypes.length > 0;
  }


  isUpdate(): boolean {
    return this.selectedMapFavorite != undefined
        && this.selectedMapFavorite.id > 0
        && this.selectedMapFavorite.userFeatureAccess?.['EditMapFavorite'] === true;
  }


  // The bar asks for this when the user clicks Create / Update.
  readonly favoriteSnapshot = (): MapFavoriteSnapshot | null => {
    const selection = this.selection;
    if (selection == undefined) {
      return null;
    }
    return {
      sensorIDs: selection.sensors.map(s => s.id),
      sensorGroupIDs: selection.sensorGroups.map(g => g.id),
      sensorReadingTypeIDs: selection.readingTypes.map(t => t.id),
      dateRange: encodeDateRange(selection.dateRangeName, selection.dateRange),
      mapDetails: this.currentMapDetails()
    };
  };


  // The bar reports which favorite is selected (picked, saved, deleted or cleared).
  onFavoriteSelected(favorite: MapFavoriteInfo | undefined): void {
    this.selectedMapFavorite = favorite;
    this.favoriteIdToSelectAfterLoad = favorite?.id;
    this.saveLastUsed();
  }


  // Apply a picked favorite: restore the selection in the left panel (which re-checks the
  // reading types and emits), then show the saved view and popups with its readings.
  applyFavorite(favorite: MapFavoriteInfo): void {
    let details: MapDetails | null = null;
    try {
      details = (favorite.mapDetails != undefined && favorite.mapDetails !== '') ? JSON.parse(favorite.mapDetails) : null;
    } catch {
      details = null;
    }

    // A favorite defines a fresh scope, so drop what was loaded for the previous one.
    this.readingsLoader.clear();
    this.pendingMapDetails = details;

    // Until the panel has restored the favorite's selection and the map has been given its
    // view, the live state is still the previous one: cache the favorite's view, not that.
    this.applyingFavorite = true;
    this.unconfirmedMapDetails = details;
    this.detailsHandedToMap = false;
    this.favoriteIdToSelectAfterLoad = undefined;

    this.selector.applySelection({
      sensorIds: (favorite.sensors ?? []).map(s => s.id),
      sensorGroupIds: (favorite.sensorGroups ?? []).map(g => g.id),
      readingTypeIds: (favorite.sensorReadingTypes ?? []).map(t => t.id),
      dateRange: favorite.dateRange,
      timeZone: details?.timeZone
    });
  }
}
